import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowButton from '../../components/arrowButton';
import Footer from '../../components/Footer';
import ApplicationFormModal from '../../components/ApplicationFormModal';
// import { getLanguageFontWithFallback } from '../../utils/fontUtils';
import { ApplicationFormProvider, useApplicationForm } from '../../contexts/ApplicationFormContext';
import { getMe, logout } from '../../api';
import './index.css';
import './index.tablet.css';
import './index.mobile.css';

// 导入字体文件，确保 webpack 打包时包含它们
import TripGeomFont from '../../fonts/ttf/TripGeom-Regular.ttf';
import TripGeomFontMedium from '../../fonts/ttf/TripGeomTF-Medium.ttf';
import TripNumberFont from '../../fonts/ttf/TripNumberTF-SemiBold.ttf';
import CtripFont from '../../fonts/ttf/CtripType-Bold-lease.ttf';

function LayoutContent({ children }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isApplicationFormOpen, closeApplicationForm } = useApplicationForm();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'zh_TW');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [currentEnterprise, setCurrentEnterprise] = useState(null);
  const [isEnterpriseDropdownOpen, setIsEnterpriseDropdownOpen] = useState(false);
  const [navReady, setNavReady] = useState(false);
  const navContainerRef = React.useRef(null);

  // 定义每个路径对应的指示器位置索引
  const navPositions = {
    '/': 0,
    '/family-friendly-promo': 1,
    '/apply-info': 2,
    '/projects': 3,
    '/news': 4,
    '/about-us': 5,
  };

  // 获取当前路径对应的导航索引
  const getNavIndex = (pathname) => {
    // 如果路径以 /projects 开头（包括 /projects/:id），返回 projects 的索引
    if (pathname.startsWith('/projects')) {
      return navPositions['/projects'];
    }
    // 如果路径以 /news 开头（包括 /news/:id），返回 news 的索引
    if (pathname.startsWith('/news')) {
      return navPositions['/news'];
    }
    // 如果路径以 /family-friendly 开头，返回 family-friendly 的索引
    if (
      pathname.startsWith('/family-friendly') ||
      pathname.startsWith('/members/') ||
      pathname.startsWith('/login') ||
      pathname.startsWith('/register') ||
      pathname.startsWith('/activate')
    ) {
      return navPositions['/family-friendly-promo'];
    }
    // 否则使用原来的映射
    return navPositions[pathname] ?? -1;
  };

  // 判断是否为首页或关于我们页（使用相同的特殊样式）
  const isHomePage = location.pathname === '/' || location.pathname === '/about-us';
  const isRegisterPage = location.pathname === '/register';
  const isFamilyFriendlyPromoPage =
    location.pathname === '/family-friendly-promo' || location.pathname.startsWith('/members/');

  // 使用 FontFace API 动态加载字体，确保字体被正确加载（对整个项目生效）
  useEffect(() => {
    const loadFonts = async () => {
      try {
        // 加载 Trip Geom TF 字体
        const tripGeomFont = new FontFace('Trip Geom TF', `url(${TripGeomFont})`, {
          weight: '400',
          style: 'normal',
          display: 'swap',
        });
        await tripGeomFont.load();
        document.fonts.add(tripGeomFont);

        // 加载 Trip Geom TF 字体
        const tripGeomFontMedium = new FontFace(
          'Trip Geom TF Medium',
          `url(${TripGeomFontMedium})`,
          {
            weight: '400',
            style: 'normal',
            display: 'swap',
          }
        );
        await tripGeomFontMedium.load();
        document.fonts.add(tripGeomFontMedium);

        // 加载 Trip Number TF 字体
        const tripNumberFont = new FontFace('Trip Number TF', `url(${TripNumberFont})`, {
          weight: '400',
          style: 'normal',
          display: 'swap',
        });
        await tripNumberFont.load();
        document.fonts.add(tripNumberFont);

        // 加载 Trip Number TF 字体
        const ctripFont = new FontFace('Ctrip Font', `url(${CtripFont})`, {
          weight: '700',
          style: 'normal',
          display: 'swap',
        });
        await ctripFont.load();
        document.fonts.add(ctripFont);
      } catch (error) {
        console.warn('字体加载失败:', error);
      }
    };

    loadFonts();
  }, []);

  useEffect(() => {
    // 预加载导航栏箭头图片，避免切换时闪烁
    const arrowImage = new Image();
    arrowImage.src = '/image/顶导箭头.png';
    const whiteArrowImage = new Image();
    whiteArrowImage.src = '/image/顶导白色箭头.png';

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 0);
      setIsEnterpriseDropdownOpen(false);
      setIsLanguageDropdownOpen(false);
    };

    // 初始化时立即检查滚动位置，避免初始状态不正确导致的闪烁
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 检查登录状态，已登录时展示企业名
  useEffect(() => {
    getMe()
      .then((res) => setCurrentEnterprise(res.result))
      .catch(() => setCurrentEnterprise(null));
  }, [location.pathname]);

  // 监听语言变化
  useEffect(() => {
    setCurrentLanguage(i18n.language || 'zh_TW');
  }, [i18n.language]);

  // 根据语言更新 html lang 属性
  useEffect(() => {
    const currentLang = i18n.language || 'zh_TW';
    document.documentElement.lang =
      currentLang === 'en' ? 'en' : currentLang === 'zh' ? 'zh-CN' : 'zh-TW';
  }, [i18n.language]);

  // 根据语言更新页面标题
  useEffect(() => {
    document.title = t('nav.title');
  }, [i18n.language, t]);

  // 计算并缓存所有导航项的位置
  useEffect(() => {
    const updateNavPositions = () => {
      if (!navContainerRef.current) return;

      const navContainer = navContainerRef.current;
      const navLinks = navContainer.querySelectorAll('a');
      const containerRect = navContainer.getBoundingClientRect();
      const isMobile = window.innerWidth <= 833;
      const indicatorWidth = isMobile ? 12 : 20;

      // 计算每个菜单项的中心位置百分比
      navLinks.forEach((link, index) => {
        const linkRect = link.getBoundingClientRect();
        const centerPosition =
          linkRect.left - containerRect.left + linkRect.width / 2 - indicatorWidth / 2;
        const percentage = (centerPosition / containerRect.width) * 100;
        navContainer.style.setProperty(`--nav-position-${index}`, `${percentage}%`);
      });

      // 设置指示器宽度
      navContainer.style.setProperty('--indicator-width', `${indicatorWidth}px`);
      setNavReady(true);
    };

    // 初始计算
    const timer = setTimeout(updateNavPositions, 100);

    // 窗口大小变化时重新计算
    window.addEventListener('resize', updateNavPositions);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateNavPositions);
    };
  }, [t]); // t 变化时重新计算（语言切换可能改变文本宽度）

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLanguageDropdownOpen && !event.target.closest('.language-selector')) {
        setIsLanguageDropdownOpen(false);
      }
      if (isEnterpriseDropdownOpen && !event.target.closest('.enterprise-selector')) {
        setIsEnterpriseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLanguageDropdownOpen, isEnterpriseDropdownOpen]);

  // 移动端侧边栏打开时防止页面滚动
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileSidebarOpen]);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    setCurrentLanguage(lang);
    setIsLanguageDropdownOpen(false);
    const params = new URLSearchParams(window.location.search);
    params.set('locale', lang);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  };

  const getLanguageDisplay = (lang) => {
    switch (lang) {
      case 'zh':
        return t('lang.simplified');
      case 'zh_TW':
        return t('lang.traditional');
      case 'en':
        return t('lang.english');
      default:
        return t('lang.simplified');
    }
  };

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  const handleMobileNavClick = () => {
    closeMobileSidebar();
  };

  // 跳转到登录页
  const navigateToLogin = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      // ignore
    }
    setCurrentEnterprise(null);
    setIsEnterpriseDropdownOpen(false);
    navigate('/login');
  };

  const handleMobileLogout = () => {
    handleLogout();
    closeMobileSidebar();
  };

  const handleMobileRegisterClick = () => {
    closeMobileSidebar();
    navigateToLogin();
  };

  let logoSrc = '/image/创生基金.png';
  if (isHomePage && !isScrolled) {
    logoSrc = '/image/创生基金-白色.png';
  }

  return (
    <div className="layout">
      {/* 移动端侧边栏遮罩 */}
      {isMobileSidebarOpen && (
        <div className="mobile-sidebar-overlay" onClick={closeMobileSidebar} />
      )}

      {/* 移动端侧边栏 */}
      <div className={`mobile-sidebar ${isMobileSidebarOpen ? 'open' : ''}`}>
        <div className="mobile-sidebar-content">
          <button className="mobile-sidebar-close" onClick={closeMobileSidebar}>
            <span></span>
            <span></span>
          </button>

          <nav className="mobile-nav">
            <NavLink to="/" onClick={handleMobileNavClick}>
              {t('nav.home')}
            </NavLink>
            <NavLink to="/family-friendly-promo" onClick={handleMobileNavClick}>
              {t('nav.familyFriendly')}
            </NavLink>
            <NavLink to="/apply-info" onClick={handleMobileNavClick}>
              {t('nav.postgraduate')}
            </NavLink>
            <NavLink to="/projects" onClick={handleMobileNavClick}>
              {t('nav.projects')}
            </NavLink>
            <NavLink to="/news" onClick={handleMobileNavClick}>
              {t('nav.news')}
            </NavLink>
            <NavLink to="/about-us" onClick={handleMobileNavClick}>
              {t('nav.about')}
            </NavLink>
          </nav>

          <div className="mobile-sidebar-footer">
            <div className="mobile-language-selector">
              <div
                className={`mobile-language-option ${currentLanguage === 'zh' ? 'active' : ''}`}
                onClick={() => {
                  handleLanguageChange('zh');
                  closeMobileSidebar();
                }}
              >
                {t('lang.simplified')}
              </div>
              <div
                className={`mobile-language-option ${currentLanguage === 'zh_TW' ? 'active' : ''}`}
                onClick={() => {
                  handleLanguageChange('zh_TW');
                  closeMobileSidebar();
                }}
              >
                {t('lang.traditional')}
              </div>
              <div
                className={`mobile-language-option ${currentLanguage === 'en' ? 'active' : ''}`}
                onClick={() => {
                  handleLanguageChange('en');
                  closeMobileSidebar();
                }}
              >
                {t('lang.english')}
              </div>
            </div>

            {/* {!isRegisterPage && (
              <ArrowButton
                text={t('nav.apply')}
                afterText={t('nav.apply.short')}
                onClick={() => {
                  closeMobileSidebar();
                  openApplicationForm();
                }}
              />
            )} */}

            <ArrowButton
              text={currentEnterprise ? t('nav.enterprise.logout') : t('nav.login')}
              afterText={currentEnterprise ? t('nav.enterprise.logout') : t('nav.login.short')}
              onClick={currentEnterprise ? handleMobileLogout : handleMobileRegisterClick}
            />
          </div>
        </div>
      </div>

      <header className={`header ${isScrolled ? 'scrolled' : ''} ${!isHomePage ? 'not-home' : ''}`}>
        {/* 移动端汉堡菜单按钮 */}
        <button className="mobile-menu-button" onClick={toggleMobileSidebar}>
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className="header-left">
          {/* <div className="icon"></div> */}
          <Link to="/" onClick={handleLogoClick}>
            <div
              className="logo"
              style={{
                fontFamily: 'Microsoft JhengHei, PingFang SC, Heiti SC, SimHei, sans-serif',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <img src={logoSrc} alt="logo" /> {t('nav.title')}{' '}
            </div>
          </Link>
        </div>
        <nav
          ref={navContainerRef}
          className={`header-center${navReady ? ' nav-ready' : ''}`}
          data-active-index={getNavIndex(location.pathname)}
        >
          {!isRegisterPage && (
            <>
              <NavLink to="/">{t('nav.home')}</NavLink>
              <NavLink to="/family-friendly-promo">{t('nav.familyFriendly')}</NavLink>
              <NavLink to="/apply-info">{t('nav.postgraduate')}</NavLink>
              <NavLink to="/projects">{t('nav.projects')}</NavLink>
              <NavLink to="/news">{t('nav.news')}</NavLink>
              <NavLink to="/about-us">{t('nav.about')}</NavLink>
              <div
                className="nav-indicator"
                style={{ backgroundColor: isFamilyFriendlyPromoPage ? '#007A7A' : '#49d1ff' }}
              />
            </>
          )}
        </nav>
        <div className="header-right">
          <div
            className="language-selector"
            onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
          >
            <span className="language-text">{getLanguageDisplay(currentLanguage)}</span>
            <div className="language-arrow-container">
              <img
                className="language-arrow language-arrow-white"
                src="/image/顶导白色箭头.png"
                alt="arrow"
                style={{
                  opacity: isScrolled || !isHomePage ? 0 : 1,
                  transform: isLanguageDropdownOpen ? 'scaleY(-1)' : 'scaleY(1)',
                  transition: 'transform 0.3s ease',
                }}
              />
              <img
                className="language-arrow language-arrow-dark"
                src="/image/顶导箭头.png"
                alt="arrow"
                style={{
                  opacity: isScrolled || !isHomePage ? 1 : 0,
                  transform: isLanguageDropdownOpen ? 'scaleY(-1)' : 'scaleY(1)',
                  transition: 'transform 0.3s ease',
                }}
              />
            </div>
            {isLanguageDropdownOpen && (
              <div className="language-dropdown">
                <div
                  className={`language-option ${currentLanguage === 'zh' ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageChange('zh');
                  }}
                >
                  {t('lang.simplified')}
                </div>
                <div
                  className={`language-option ${currentLanguage === 'zh_TW' ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageChange('zh_TW');
                  }}
                >
                  {t('lang.traditional')}
                </div>
                <div
                  className={`language-option ${currentLanguage === 'en' ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageChange('en');
                  }}
                >
                  {t('lang.english')}
                </div>
              </div>
            )}
          </div>
          {currentEnterprise ? (
            <div
              className="enterprise-selector"
              onClick={() => setIsEnterpriseDropdownOpen(!isEnterpriseDropdownOpen)}
            >
              <span className="enterprise-name">{currentEnterprise.companyName}</span>
              <img
                className="enterprise-arrow"
                src="/image/顶导箭头.png"
                alt="arrow"
                style={{
                  transform: isEnterpriseDropdownOpen ? 'scaleY(-1)' : 'scaleY(1)',
                  transition: 'transform 0.3s ease',
                }}
              />
              {isEnterpriseDropdownOpen && (
                <div className="enterprise-dropdown">
                  <div
                    className="enterprise-dropdown-item enterprise-dropdown-item--active"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEnterpriseDropdownOpen(false);
                      navigate('/family-friendly/overview');
                    }}
                  >
                    {t('nav.enterprise.overview')}
                  </div>
                  <div
                    className="enterprise-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEnterpriseDropdownOpen(false);
                      navigate('/family-friendly/company-info');
                    }}
                  >
                    {t('nav.enterprise.companyInfo')}
                  </div>
                  <div className="enterprise-dropdown-divider" />
                  <div
                    className="enterprise-dropdown-item enterprise-dropdown-item--logout"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout();
                    }}
                  >
                    {t('nav.enterprise.logout')}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <ArrowButton
              text={t('nav.login')}
              afterText={t('nav.login.short')}
              onClick={navigateToLogin}
            />
          )}
        </div>
      </header>
      <main className="main-content">{children}</main>
      <ApplicationFormModal isOpen={isApplicationFormOpen} onClose={closeApplicationForm} />
      <Footer isPromo={isFamilyFriendlyPromoPage} />
    </div>
  );
}

function Layout({ children }) {
  return (
    <ApplicationFormProvider>
      <LayoutContent>{children}</LayoutContent>
    </ApplicationFormProvider>
  );
}

export default Layout;
